import { apiClient } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import type { ConfirmRequest } from '../constants/partnerStatus';

// 시공반장(시공파트너) 후보 조회 · 회신 결과 조회 — 응답 타입은 52-Planner 서버 DTO 그대로.
// 날짜(LocalDate)는 "YYYY-MM-DD", 일시(LocalDateTime)는 "YYYY-MM-DDTHH:mm:ss" 문자열.
// 발송은 scheduleApi.sendForemanConfirm 을 쓴다(이 파일에서 재정의하지 않음).

// ── 후보 조회 (GET /schedule/tasks/{taskId}/partner-candidates) ──────────────

export interface PartnerCandidateRegion { code: string; name: string }

export interface PartnerCandidateConflict {
  siteId: number | null;
  siteName: string | null;
  startDate: string;
  endDate: string;
}

export type WorkTypeMatch = 'MAIN' | 'SUB' | 'DESIGNATED';

export interface PartnerCandidate {
  partnerId: number;
  partnerName: string;
  mobile: string | null;
  /** INDIVIDUAL | COMPANY 등 서버 값 — 업체 여부는 company 사용 */
  partnerType: string | null;
  company: boolean;
  workTypeMatch: WorkTypeMatch | string;
  orderZero: boolean;
  avgRating: number | null;
  reviewCount: number | null;
  /** 최근 협업일(yyyy-MM-dd) — 후보 정렬 두 번째 기준(협업 횟수 다음). 없으면 null */
  lastCollabDate?: string | null;
  /** 연락 방식(SIF-3) — Y=알림톡 발송-회신 / N=결과만 직접등록. 미제공이면 null */
  commYn?: string | null;
  collabCount: number;
  replyWithinTwoHoursPercent: number | null;
  /** 보고준수 % (정수 0~100). 집계가 없거나 조회 실패면 null */
  reportComplianceRate: number | null;
  /** 하자보고 % (정수 0~100). 집계가 없거나 조회 실패면 null */
  defectReportRate: number | null;
  regions: PartnerCandidateRegion[];
  regionMatch: boolean;
  dealSuspended: boolean;
  /** 이 공종의 현재 배정 반장 */
  current: boolean;
  declined: boolean;
  declinedDtm: string | null;
  scheduleConflict: boolean;
  conflicts: PartnerCandidateConflict[] | null;
  alreadySent: boolean;
  sendCount: number;
  lastSentDtm: string | null;
  sendFailed: boolean;
}

export interface PartnerCandidateExcluded { partnerId: number; partnerName: string; code: string }

export interface PartnerCandidatesResult {
  taskId: number;
  scheduleId: number;
  siteId: number;
  workTypeCode: string;
  workTypeName: string;
  taskStartDate: string;
  taskEndDate: string;
  partnerStatus: string;
  currentPartnerId: number | null;
  /** 서버가 정렬해 내려준 순서 그대로(주작업→우선→별점→협업, 일정 겹침 맨 아래) — FE 재정렬 금지 */
  candidates: PartnerCandidate[];
  excluded: PartnerCandidateExcluded[];
}

export async function getPartnerCandidates(taskId: number): Promise<PartnerCandidatesResult> {
  const res = await apiClient.get<ApiResponse<PartnerCandidatesResult>>(`/schedule/tasks/${taskId}/partner-candidates`);
  return res.data.data;
}

// ── 회신 결과 (GET /schedule/schedules/{scheduleId}/foreman-confirm-results) ──

export type DurationUnit = 'HOUR' | 'DAY'

export interface ForemanConfirmSettings { replyDuration: number; durationUnit: DurationUnit; autoNext: boolean; autoNextMaxCnt: number }

export interface ForemanConfirmSummary {
  /** 일정확인을 받은(전달된) 파트너 수 */
  sentPartnerCount: number;
  confirmedCount: number;
  waitingCount: number;
  actionCount: number;
  autoScheduledCount: number;
  autoSendCount: number;
  expireToUnfixedCount: number;
}

export interface ForemanNextPartner { partnerId: number; partnerName: string }

/** 지금 처리할 일 사유 */
export type ForemanActionReason = 'SEND_FAILED' | 'DELIVERY_DEAD' | 'UNFIXED' | 'DECLINED' | 'UNASSIGNABLE' | 'EXPIRED';
/** 자동 처리 예정 계획 */
export type ForemanPlan = 'AUTO_SEND' | 'UNFIXED_ON_EXPIRE';
export type ForemanPlanReason = 'AUTO_OFF' | 'LIMIT_REACHED' | 'NO_CANDIDATE';

export interface ForemanConfirmTaskItem {
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  taskStartDate: string;
  taskEndDate: string;
  partnerStatus: string;
  partnerId: number | null;
  partnerName: string | null;
  partnerPhone: string | null;
  /** 지금 처리할 일(🔴)에서만 */
  reason: ForemanActionReason | null;
  /** reason=SEND_FAILED 일 때 NO_CONTACT | SEND_FAILED */
  failReason: string | null;
  attemptCount: number;
  latestHistoryId: number | null;
  latestSendMethod: string | null;
  sentDtm: string | null;
  respondedDtm: string | null;
  replyExpireDtm: string | null;
  /** 자동 처리 예정(🟡)에서만 */
  scheduledAt: string | null;
  /** 🔴에서만 */
  recommendedPartner: ForemanNextPartner | null;
  /** 🟡 AUTO_SEND에서만 */
  autoNextPartner: ForemanNextPartner | null;
  resendAvailable: boolean;
  plan: ForemanPlan | null;
  reasonCode: ForemanPlanReason | null;
}

export type ForemanHistoryStatus = 'WAITING' | 'CONFIRMED' | 'DECLINED' | 'EXPIRED' | 'CLOSED' | 'SEND_FAILED';

export interface ForemanConfirmHistoryItem {
  historyId: number;
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  taskStartDate: string;
  taskEndDate: string;
  attemptNo: number | null;
  partnerId: number | null;
  partnerName: string | null;
  partnerPhone: string | null;
  /** MANUAL | AUTO | RESEND | CHANGE */
  sendMethod: string | null;
  sentDtm: string | null;
  respondedDtm: string | null;
  replyExpireDtm: string | null;
  status: ForemanHistoryStatus;
  responseResult: string | null;
  /** EXPIRED | REPLACED | CHANGED (status=CLOSED 일 때) */
  closedReason: string | null;
  failReason: string | null;
  deliveryFailed: boolean;
  latest: boolean;
}

export interface ForemanConfirmResults {
  scheduleId: number;
  siteId: number;
  siteName: string | null;
  asOf: string;
  settings: ForemanConfirmSettings;
  summary: ForemanConfirmSummary;
  actionRequired: ForemanConfirmTaskItem[];
  autoScheduled: ForemanConfirmTaskItem[];
  /** 최신 발송순 — 서버 순서 그대로 */
  histories: ForemanConfirmHistoryItem[];
}

export async function getForemanConfirmResults(scheduleId: number): Promise<ForemanConfirmResults> {
  const res = await apiClient.get<ApiResponse<ForemanConfirmResults>>(`/schedule/schedules/${scheduleId}/foreman-confirm-results`);
  return res.data.data;
}

// ── 알림톡 예시 미리보기 (GET /schedule/foreman-confirm-sends/notice-previews) ──
// 문구는 서버 렌더러가 만든 그대로 — FE는 표시만 한다.
export type NoticePreviewKind = 'CONFIRM' | 'CLOSED_EXPIRED' | 'CLOSED_REPLACED' | 'CHANGE' | 'CANCEL';

export interface NoticePreviewItem {
  kind: NoticePreviewKind;
  content: string;
  buttons: string[];
}

export async function getNoticePreviews(taskId: number): Promise<NoticePreviewItem[]> {
  const res = await apiClient.get<ApiResponse<{ items: NoticePreviewItem[] }>>('/schedule/foreman-confirm-sends/notice-previews', { params: { taskId } });
  return res.data.data.items;
}

// ── 시공파트너 일정확인 설정 (GET·PATCH·DELETE /schedule/settings/foreman-confirm) ──
// 사업장(X-Comp-Id)·사용자(X-User-Id)는 게이트웨이가 주입. PATCH는 보낸 필드만 갱신(생략=기존값 유지).

export interface ForemanConfirmSetting {
  /** 답장 기한 1~72 (단위는 durationUnit) */
  replyDuration: number;
  /** 설정 화면은 'HOUR' 만 보낸다 */
  durationUnit: DurationUnit;
  autoNext: boolean;
  /** 자동 발송 최대 순위 — 2 고정(2순위 1차례). 화면에서 바꾸지 않는다 */
  autoNextMaxCnt: number;
  /** 저장된 설정이 없어 기본값(2·true·2)을 쓰는 중 */
  isDefault: boolean;
}

export type ForemanConfirmSettingPatch = Partial<Pick<ForemanConfirmSetting, 'replyDuration' | 'durationUnit' | 'autoNext' | 'autoNextMaxCnt'>>;

export async function getForemanConfirmSetting(): Promise<ForemanConfirmSetting> {
  const res = await apiClient.get<ApiResponse<ForemanConfirmSetting>>('/schedule/settings/foreman-confirm');
  return res.data.data;
}

export async function patchForemanConfirmSetting(patch: ForemanConfirmSettingPatch): Promise<ForemanConfirmSetting> {
  const res = await apiClient.patch<ApiResponse<ForemanConfirmSetting>>('/schedule/settings/foreman-confirm', patch);
  return res.data.data;
}

export async function resetForemanConfirmSetting(): Promise<ForemanConfirmSetting> {
  const res = await apiClient.delete<ApiResponse<ForemanConfirmSetting>>('/schedule/settings/foreman-confirm');
  return res.data.data;
}

// ── 확정 반장 변경 (POST /schedule/tasks/{taskId}/partner-changes) ───────────────
// 서버 ForemanChangeService — 사유 필수, 이전 반장에게 배정 취소 안내, 변경 이력 기록, 착공 임박 경고.

/** 변경 사유 코드(E01_PARTNER_CHANGE_REASON). 한글 라벨은 constants/foremanPlan 의 PARTNER_CHANGE_REASONS. */
export type PartnerChangeReasonCode = 'CUSTOMER_REQUEST' | 'PARTNER_CIRCUMSTANCE' | 'QUALITY_HISTORY' | 'ETC';

export interface ForemanChangeRequest {
  newPartnerId: number;
  reasonCode: PartnerChangeReasonCode;
  /** true=새 반장에게 일정확인을 바로 발송(WAITING) · false=지정만(ASSIGNED) */
  sendNow: boolean;
  /** 착공 D-2 이내 즉시 확정 확인 후 재호출 시 true (서버 409 IMMEDIATE_CONFIRM_REQUIRED 에 대한 확인) */
  confirmImmediate?: boolean;
}

export interface ForemanChangeResult {
  taskId: number;
  previousPartnerId: number | null;
  partnerId: number;
  partnerStatus: string;
  /** 착공 D-2 이내라 일정확인 없이 즉시 확정됨(partnerStatus=CONFIRMED, sent 는 무의미) */
  fixed?: boolean;
  reasonCode: string;
  sendNow: boolean;
  /** sendNow=true 일 때만 */
  sent: { historyId: number; attemptNo: number | null; sentDtm: string; replyExpireDtm: string | null } | null;
  /** 착공이 임박한 공종 */
  imminentWarning: boolean;
  /** 이전 반장 배정 취소 안내 호출 완료 여부(호출 완료일 뿐 도달 보장 아님) */
  cancelNoticed: boolean;
  /** sendNow=false 일 때 true: 취소 안내는 새 반장에게 일정확인을 보낼 때 함께 발송(서버 대기 기록) */
  cancelNoticePending?: boolean;
  closedRequests: { historyId: number; partnerId: number | null; closedReason: string }[];
}

export type ForemanChangeErrorCode =
  | 'FOREMAN_CHANGE_REASON_INVALID'
  | 'FOREMAN_CHANGE_NOT_ALLOWED'
  | 'FOREMAN_CHANGE_SAME_PARTNER'
  | 'FOREMAN_CHANGE_PARTNER_INVALID'
  | 'FOREMAN_CONTACT_MISSING'
  | 'FOREMAN_CONFIRM_SEND_FAILED'
  | 'CONFIRMED_FOREMAN_RELEASE_NOT_ALLOWED'
  | 'CONFLICT'
  | 'IMMEDIATE_CONFIRM_REQUIRED'
  | 'UNKNOWN';

const FOREMAN_CHANGE_ERROR_CODES: readonly string[] = [
  'FOREMAN_CHANGE_REASON_INVALID', 'FOREMAN_CHANGE_NOT_ALLOWED', 'FOREMAN_CHANGE_SAME_PARTNER',
  'FOREMAN_CHANGE_PARTNER_INVALID', 'FOREMAN_CONTACT_MISSING', 'FOREMAN_CONFIRM_SEND_FAILED',
  'CONFIRMED_FOREMAN_RELEASE_NOT_ALLOWED', 'CONFLICT', 'IMMEDIATE_CONFIRM_REQUIRED',
];

/** 확정 반장 변경·반장 해제 API 오류. 알 수 없는 오류는 code=UNKNOWN. */
export class ForemanChangeError extends Error {
  readonly code: ForemanChangeErrorCode;
  readonly httpStatus: number | null;
  /** 서버 응답 message (IMMEDIATE_CONFIRM_REQUIRED 확인 모달 본문에 사용) */
  readonly serverMessage: string | null;
  /** 서버 응답 data (IMMEDIATE_CONFIRM_REQUIRED 의 immediateDays 등) */
  readonly serverData: { immediateDays?: number } | null;
  constructor(code: ForemanChangeErrorCode, httpStatus: number | null, serverMessage: string | null = null, serverData: { immediateDays?: number } | null = null) {
    super(code);
    this.name = 'ForemanChangeError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.serverMessage = serverMessage;
    this.serverData = serverData;
  }
}

function toForemanChangeError(err: unknown): ForemanChangeError {
  const res = (err as { response?: { status?: number; data?: { code?: string; message?: string; data?: { immediateDays?: number } | null } } })?.response;
  const code = res?.data?.code;
  const known = code && FOREMAN_CHANGE_ERROR_CODES.includes(code) ? (code as ForemanChangeErrorCode) : 'UNKNOWN';
  return new ForemanChangeError(known, res?.status ?? null, res?.data?.message ?? null, res?.data?.data ?? null);
}

/** 확정(CONFIRMED) 반장을 다른 반장으로 바꾼다. 실패 시 {@link ForemanChangeError}. */
export async function changeConfirmedPartner(taskId: number, req: ForemanChangeRequest): Promise<ForemanChangeResult> {
  try {
    const res = await apiClient.post<ApiResponse<ForemanChangeResult>>(`/schedule/tasks/${taskId}/partner-changes`, req);
    return res.data.data;
  } catch (err) {
    throw toForemanChangeError(err);
  }
}

// ── 반장 해제 (DELETE /schedule/tasks/{taskId}/partners/{partnerId}) ──────────────
// 허용 상태 ASSIGNED·WAITING·DECLINED·UNFIXED. 확정 반장은 422 CONFIRMED_FOREMAN_RELEASE_NOT_ALLOWED, 반장 불일치 409 CONFLICT.

export interface ForemanReleaseResult {
  taskId: number;
  releasedPartnerId: number;
  previousPartnerStatus: string;
  partnerStatus: string;
  closedRequests: { historyId: number; partnerId: number | null; closedReason: string }[];
}

export async function releasePartner(taskId: number, partnerId: number): Promise<ForemanReleaseResult> {
  try {
    const res = await apiClient.delete<ApiResponse<ForemanReleaseResult>>(`/schedule/tasks/${taskId}/partners/${partnerId}`);
    return res.data.data;
  } catch (err) {
    throw toForemanChangeError(err);
  }
}

// ── 일정 변경 카톡 (저장 응답 dateChanges · POST /schedule/schedules/{id}/schedule-change-notices) ──

/** 날짜 저장 응답 data.dateChanges[] 1건 — notifiable=true 인 건만 확인 창의 체크 대상(서버 판정 그대로). */
export interface DateChange {
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  oldStartDate: string | null;
  oldEndDate: string | null;
  newStartDate: string | null;
  newEndDate: string | null;
  partnerId: number | null;
  partnerName: string | null;
  /** CONFIRMED | WAITING 등 서버 상태 코드 */
  partnerStatus: string | null;
  notifiable: boolean;
}

export interface ScheduleChangeNoticeItem {
  taskId: number;
  oldStartDate: string | null;
  oldEndDate: string | null;
}

/** 서버 failReason — 재검증 사유 · 발송 실패 코드 · 오류 코드. 그 외 값도 올 수 있다. */
export type ScheduleChangeNoticeFailReason =
  | 'TASK_NOT_FOUND' | 'NOT_IN_SCHEDULE' | 'FOREMAN_NOT_ASSIGNED' | 'NOT_NOTIFIABLE' | 'DATE_NOT_CHANGED'
  | 'INVALID_OLD_DATE' | 'DUPLICATE_ITEM' | 'NO_CONTACT' | 'SEND_FAILED' | 'CONFLICT' | 'SEND_NOT_ALLOWED'
  | 'INTERNAL_SERVER_ERROR';

export interface ScheduleChangeNoticeItemResult {
  taskId: number;
  sent: boolean;
  failReason: ScheduleChangeNoticeFailReason | string | null;
  historyId: number | null;
  attemptNo: number | null;
  partnerId: number | null;
  partnerStatus: string | null;
  sentDtm: string | null;
  replyExpireDtm: string | null;
}

export interface ScheduleChangeNoticeResult {
  requested: number;
  sent: number;
  failed: number;
  items: ScheduleChangeNoticeItemResult[];
}

/** 서버 1회 최대 100건 */
const SCHEDULE_CHANGE_NOTICE_MAX = 100;

/** 체크한 공종에만 변경 카톡 발송. 건별 결과는 items[] (실패해도 HTTP 오류가 아님). 100건 초과는 나눠 보내 결과를 합친다. */
export async function sendScheduleChangeNotices(
  scheduleId: number,
  items: ScheduleChangeNoticeItem[],
): Promise<ScheduleChangeNoticeResult> {
  const merged: ScheduleChangeNoticeResult = { requested: 0, sent: 0, failed: 0, items: [] };
  for (let i = 0; i < items.length; i += SCHEDULE_CHANGE_NOTICE_MAX) {
    const res = await apiClient.post<ApiResponse<ScheduleChangeNoticeResult>>(
      `/schedule/schedules/${scheduleId}/schedule-change-notices`,
      { items: items.slice(i, i + SCHEDULE_CHANGE_NOTICE_MAX) },
    );
    const d = res.data.data;
    merged.requested += d.requested;
    merged.sent += d.sent;
    merged.failed += d.failed;
    merged.items.push(...(d.items ?? []));
  }
  return merged;
}

/** 공종 날짜 일괄 저장 항목. syncOrderDates=true 이면 시작일이 바뀐 이 공종의 발주일·입고일을 새 착공일 기준으로 함께 옮긴다(발주 일정이 있는 공종만, 입고 완료 공종 제외). */
export interface TaskDatePatchItem {
  taskId: number;
  startDate: string;
  endDate: string;
  syncOrderDates?: boolean;
}

/** 발주·입고 동반 이동 결과(공종별 이동 후 값 — 실제로 바뀐 공종만). 날짜는 'YYYY-MM-DD' */
export interface OrderDateChange {
  taskId: number;
  oldOrderDate: string | null;
  newOrderDate: string | null;
  oldDeliveryDate: string | null;
  newDeliveryDate: string | null;
}

/** 동반 이동 경고 코드(BE OrderScheduleChangeService.WARN_*). 화면은 ORDER_ALREADY_SENT·DELIVERY_RECEIVED 2종만 안내한다. */
export type OrderWarningCode =
  | 'ORDER_ALREADY_SENT' | 'ORDER_AFTER_DELIVERY' | 'DELIVERY_AFTER_START' | 'STALE_DELIVERY' | 'DELIVERY_RECEIVED';

export interface OrderDateWarning {
  taskId: number;
  code: OrderWarningCode | string;
}

export interface TaskDatesPatchResult {
  dateChanges: DateChange[];
  orderChanges: OrderDateChange[];
  orderWarnings: OrderDateWarning[];
}

/** 공종 날짜 일괄 저장(PATCH /schedule/schedules/{id}/tasks) — 응답의 dateChanges·orderChanges·orderWarnings 를 함께 돌려준다. */
export async function patchScheduleTaskDates(
  scheduleId: number,
  tasks: TaskDatePatchItem[],
  headers?: Record<string, string | undefined>,
  /** true 일 때만 서버가 스케줄 시작·종료일(과 공사완료 마커)을 함께 변경한다 — 확인창에서 '진행'을 눌렀을 때만 true */
  syncSchedulePeriod?: boolean,
): Promise<TaskDatesPatchResult> {
  const res = await apiClient.patch<ApiResponse<{
    dateChanges?: DateChange[] | null;
    orderChanges?: OrderDateChange[] | null;
    orderWarnings?: OrderDateWarning[] | null;
  }>>(
    `/schedule/schedules/${scheduleId}/tasks`,
    { tasks, ...(syncSchedulePeriod ? { syncSchedulePeriod: true } : {}) },
    headers ? { headers } : undefined,
  );
  const d = res.data.data;
  return {
    dateChanges: d?.dateChanges ?? [],
    orderChanges: d?.orderChanges ?? [],
    orderWarnings: d?.orderWarnings ?? [],
  };
}

/**
 * 날짜 일괄 저장 실패 문구(간트·캘린더·리스트 공용) — 서버 오류 코드별.
 * INVALID_DATE·TASK_START_ON_NON_WORKDAY 는 둘 다 422.
 */
export function taskDateSaveErrorMessage(err: unknown, fallback = '저장 중 오류가 발생했습니다.'): string {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  switch (code) {
    case 'INVALID_DATE': return '종료일이 시작일보다 앞설 수 없어요';
    case 'TASK_START_ON_NON_WORKDAY': return '일요일·공휴일에는 시작할 수 없어요';
    default: return fallback;
  }
}

// ── 오늘 할일 알림 시각 (GET·PATCH·DELETE /schedule/settings/today-todo-notification) ──
// PATCH·DELETE 는 OWNER 만(STAFF 403).

export interface TodayTodoNotiSetting {
  enabled: boolean;
  /** 0~23 (저장값이 없으면 기본 7) */
  hour: number;
  /** 0·10·20·30·40·50 — 10분 단위 (저장값이 없으면 0) */
  minute: number;
  /** 저장된 설정이 없어 기본값(켜짐·07:00)을 쓰는 중 */
  isDefault: boolean;
}

export type TodayTodoNotiPatch = Partial<Pick<TodayTodoNotiSetting, 'enabled' | 'hour' | 'minute'>>;

export async function getTodayTodoNotiSetting(): Promise<TodayTodoNotiSetting> {
  const res = await apiClient.get<ApiResponse<TodayTodoNotiSetting>>('/schedule/settings/today-todo-notification');
  return res.data.data;
}

export async function patchTodayTodoNotiSetting(patch: TodayTodoNotiPatch): Promise<TodayTodoNotiSetting> {
  const res = await apiClient.patch<ApiResponse<TodayTodoNotiSetting>>('/schedule/settings/today-todo-notification', patch);
  return res.data.data;
}

export async function resetTodayTodoNotiSetting(): Promise<TodayTodoNotiSetting> {
  const res = await apiClient.delete<ApiResponse<TodayTodoNotiSetting>>('/schedule/settings/today-todo-notification');
  return res.data.data;
}

// ── 공종 일정확인 요청 요약 단건 (GET /schedule/tasks/{taskId}/foreman-confirm-request) ──
// 발송·변경 성공 직후 화면 갱신용. 전달된 요청이 없으면 null.

export async function getForemanConfirmRequest(taskId: number): Promise<ConfirmRequest | null> {
  const res = await apiClient.get<ApiResponse<ConfirmRequest | null>>(`/schedule/tasks/${taskId}/foreman-confirm-request`);
  return res.data.data ?? null;
}

// ── 자동매칭 근거 (GET /schedule/tasks/{taskId}/assign-evidence) ──
// 자동배정(AUTO) 공종의 배정 근거. available=false 면 이유별 한 줄만 보여준다(HTTP 200).

export type AssignEvidenceUnavailableReason = 'NOT_AUTO' | 'NO_EVIDENCE' | 'LEGACY_VERSION';

export interface AssignEvidenceCondition {
  code: string;
  label: string;
  /** true=충족 · false=미충족 · null=건너뜀 */
  passed: boolean | null;
  detail: string | null;
}

export interface AssignEvidenceCandidate {
  rank: number;
  partnerId: number;
  partnerName: string;
  selected: boolean;
  selectable: boolean;
  scheduleConflict: boolean;
  conflicts: { siteName: string | null; startDate: string; endDate: string }[] | null;
  avgRating: number | null;
  /** 최근 12개월 협업 횟수 — 순위 1번째 기준 */
  collabCount: number | null;
  /** 최근 협업일(yyyy-MM-dd) — 순위 2번째 기준. v3 이하 근거엔 없음 */
  lastCollabDate: string | null;
  /** 보고준수율 · 하자율(0~1) — 순위에 쓰지 않는 참고 값. v3 이하 근거엔 없음 */
  reportRate: number | null;
  defectRate: number | null;
}

export interface AssignEvidenceDetail {
  version: number;
  /** MAIN(주시공파트너 지정) · CONTINUITY(앞선 공정과 같은 반장) · SCORED(우선순위 선정 — 코드값은 예전 그대로) */
  reason: 'MAIN' | 'CONTINUITY' | 'SCORED' | string;
  period: { start: string; end: string } | null;
  summary: { headline: string; detail: string | null };
  conditions: AssignEvidenceCondition[];
  showCandidates: boolean;
  candidates: AssignEvidenceCandidate[];
  triedCount: number;
}

export interface AssignEvidence {
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  partnerId: number | null;
  assignSource: string | null;
  available: boolean;
  unavailableReason: AssignEvidenceUnavailableReason | null;
  /** available=false 면 null */
  evidence: AssignEvidenceDetail | null;
}

export async function getAssignEvidence(taskId: number): Promise<AssignEvidence> {
  const res = await apiClient.get<ApiResponse<AssignEvidence>>(`/schedule/tasks/${taskId}/assign-evidence`);
  return res.data.data;
}
